/**
 * La mappa delle aziende della console (27/10/2026, Simone: «una modalità mappa … dove sono i tenant che usano il
 * sistema, quanti account hanno attivi, con la differenziazione tra gratuiti, base, pro e business, quanti sono utenti
 * attivi … condivisibile a qualcuno che non ha un accesso nel nostro gestionale»).
 *
 * Qui stanno le REGOLE che decidono cosa si vede e la forma dei dati, comuni al server (che compone la mappa,
 * `functions/src/services/mappaAziende/`) e alla console (che la disegna e la filtra). Una sola fonte: la console non
 * ricalcola niente di suo, e il link condiviso mostra esattamente ciò che queste funzioni lasciano passare.
 *
 * Senza import: si copia così com'è nel server (`functions/package.json`, `sync:shared`, cartella `admin`).
 */

// ───────────────────────────────────────────────────────────── livelli e stati

/** I livelli della mappa: il piano dell'azienda e il livello di ogni suo account. */
export const LIVELLI_MAPPA = ['free', 'base', 'pro', 'business'] as const;
export type LivelloMappa = (typeof LIVELLI_MAPPA)[number];

/**
 * Il livello di un posto, come lo scrive `seat.used_by_tier` (non esistono posti gratuiti). `presenze` (02/11/2026) è il
 * posto FUORI DALLA SCALA di chi timbra soltanto: un posto occupato come gli altri, ma non un piano dell'azienda.
 */
export type LivelloDelPosto = 'base' | 'pro' | 'business' | 'presenze';

/**
 * Il livello di un ACCOUNT sulla mappa: i livelli della mappa (il piano dell'azienda) più i posti fuori dalla scala, che
 * si contano a parte. Chi disegna per livello scorre `LIVELLI_MAPPA`: un account Presenze conta nel totale
 * (`account_totale`) e nella sua chiave, e non prende il colore di un piano che non ha.
 */
export type LivelloDellAccount = LivelloMappa | 'presenze';

/**
 * Il piano dell'azienda come livello della mappa. Stessa regola di `pianoDellAzienda` del server
 * (`_shared/capabilities/tierCapabilities.ts`): assente o sconosciuto → Base, il ripiego conservativo di sempre.
 */
export function livelloDellAzienda(tier: unknown): LivelloMappa {
  return tier === 'free' || tier === 'pro' || tier === 'business' ? tier : 'base';
}

/**
 * Il livello di un account sulla mappa. I posti sono Base, Pro o Business; un'azienda col piano gratuito però non ha
 * posti a pagamento (il conteggio del server mette i suoi account nel secchio Base), e i suoi account si contano
 * «gratuiti»: è la differenza che Simone ha chiesto di vedere.
 */
export function livelloDellAccount(livelloAzienda: LivelloMappa, posto: LivelloDelPosto): LivelloDellAccount {
  return livelloAzienda === 'free' ? 'free' : posto;
}

/** Lo stato dell'azienda sulla mappa: di predefinito si vedono solo le attive (Simone, 27/10/2026). */
export const STATI_MAPPA = ['attiva', 'pagamenti_in_ritardo', 'sospesa', 'bloccata', 'cancellata'] as const;
export type StatoMappa = (typeof STATI_MAPPA)[number];

/**
 * Lo stato dell'azienda dai campi del tenant. Il blocco dell'accesso messo dal super utente (`blocco_accesso`,
 * `services/aziende/accessoAzienda.ts`) vince su tutto: un'azienda bloccata è anche «sospesa», ma il motivo che conta
 * è il blocco. `trialing` (scritto su alcune aziende in prova) e l'assenza del campo valgono «attiva», come
 * nell'elenco delle aziende della console.
 */
export function statoDellAzienda(tenant: { tenant_status?: unknown; blocco_accesso?: unknown }): StatoMappa {
  if (tenant.blocco_accesso && typeof tenant.blocco_accesso === 'object') return 'bloccata';
  switch (tenant.tenant_status) {
    case 'suspended':
      return 'sospesa';
    case 'canceled':
    case 'deleted':
      return 'cancellata';
    case 'grace_period':
    case 'restricted':
      return 'pagamenti_in_ritardo';
    default:
      return 'attiva';
  }
}

// ───────────────────────────────────────────────────────────── chi rifornisce chi

/**
 * I fornitori di materiale di un'azienda (28/10/2026, Simone: «questo collegamento che c'è tra Forniture Fotovoltaiche
 * e le aziende convenzionate… deve vedersi anche nella mappa»). È l'UNICO punto in cui la mappa legge il legame: oggi
 * da `config.fornitura.fornitore_id` (al più un fornitore), con l'accordo attivo e senza contare chi punta a sé stesso.
 * Quando la convenzione diventerà un documento fra le due aziende (`forniture_condizioni/{fornitore__cliente}`,
 * modulo Forniture) cambia solo il corpo di questa funzione, e un'azienda potrà averne più d'uno.
 */
export function fornitoriDellAzienda(tenant: { config?: unknown }, tenantId: string): string[] {
  const config = tenant.config && typeof tenant.config === 'object' ? (tenant.config as Record<string, unknown>) : {};
  const fornitura = config.fornitura && typeof config.fornitura === 'object' ? (config.fornitura as Record<string, unknown>) : null;
  if (!fornitura || fornitura.attiva === false) return [];
  const id = typeof fornitura.fornitore_id === 'string' ? fornitura.fornitore_id.trim() : '';
  return id && id !== tenantId ? [id] : [];
}

/**
 * Chi ha portato l'azienda su YumaOS: il suo partner nel programma partner (`platform_partner_abbinamenti`), MAI la
 * fornitura (Vivisolar e Power Service comprano da Forniture Fotovoltaiche ma sono aziende di Simone, 28/10/2026). Lo
 * calcola il server (`services/mappaAziende/chiHaPortato.ts`) con le regole del programma partner.
 */
export interface PortataDa {
  /** Il nome del partner (una persona o una società). */
  partner: string;
  /** L'azienda di YumaOS del partner (stessa partita IVA), se c'è: da lì parte l'arco sulla carta. */
  azienda: string | null;
}

// ───────────────────────────────────────────────────────────── persone attive

/**
 * Una persona è «attiva» se ha usato il sistema negli ultimi 7 giorni. La misura è quella dell'accesso
 * (`metadata.lastRefreshTime` / `lastSignInTime` di Firebase Auth, il più recente dei due): un'app aperta rinnova il
 * pass ogni ora, quindi chi lavora davvero compare; chi ha solo l'account no. È la stessa misura dell'«ultima attività»
 * del blocco dell'accesso.
 */
export const GIORNI_PERSONE_ATTIVE = 7;
const GIORNO_MS = 86_400_000;

export function eAttivaDiRecente(
  ultimaAttivita: string | null | undefined,
  adesso: Date,
  giorni: number = GIORNI_PERSONE_ATTIVE,
): boolean {
  if (!ultimaAttivita) return false;
  const quando = Date.parse(ultimaAttivita);
  if (!Number.isFinite(quando)) return false;
  return adesso.getTime() - quando <= giorni * GIORNO_MS;
}

/**
 * Una persona è «al lavoro ADESSO» se la sta USANDO (`statoDiPresenza` di `shared/sessioni/presenza.ts`, 28/10/2026):
 * nel gestionale un'azione vera negli ultimi `MINUTI_SENZA_AZIONI` minuti, nelle app native (che battono solo in primo
 * piano) l'app davanti. Chi ha solo la pagina aperta, ferma, si vede a parte (`pagina_aperta`, solo console). Prima
 * contava il battito, che il gestionale manda con la pagina aperta: una scheda dimenticata da quindici giorni risultava
 * «al lavoro adesso». Le web app (cornice-app-web) si registrano come app (`mobile`) e usano lo stesso battito del
 * gestionale: contano dalla loro prossima pubblicazione con l'ora dell'ultima azione.
 */
export { MINUTI_SENZA_AZIONI, statoDiPresenza, type StatoDiPresenza } from '../sessioni/presenza';

/** Il più recente di due istanti ISO (null se mancano entrambi). */
export function istantePiuRecente(a: string | null | undefined, b: string | null | undefined): string | null {
  const ta = a ? Date.parse(a) : NaN;
  const tb = b ? Date.parse(b) : NaN;
  if (!Number.isFinite(ta) && !Number.isFinite(tb)) return null;
  if (!Number.isFinite(tb)) return a ?? null;
  if (!Number.isFinite(ta)) return b ?? null;
  return ta >= tb ? (a as string) : (b as string);
}

// ───────────────────────────────────────────────────────────── la forma dei dati

export interface PosizioneSullaMappa {
  lat: number;
  lng: number;
  /** `indirizzo` = la sede trovata alla via; `comune` = Google ha trovato solo il comune. */
  precisione: 'indirizzo' | 'comune';
}

export type ContiPerLivello = Partial<Record<LivelloDellAccount, number>>;

/**
 * Una sede dell'azienda sulla mappa. Le sedi sono quelle OPERATIVE (`tenants.sedi_operative`, dove l'azienda lavora:
 * uffici, magazzini, showroom), non la sede legale (Simone, 27/10/2026: «non devi mettere la sede legale, devi mettere
 * le sedi operative… se le aziende hanno più sedi operative, potresti metterle entrambe, però fa sempre riferimento a
 * un'unica azienda»). Solo un'azienda che non ne ha scritta nessuna si vede alla sede legale, e lo dice (`legale`).
 */
export interface SedeSullaMappa {
  /** Stabile dentro l'azienda: l'id della sede, o `legale`. */
  chiave: string;
  /** Il nome che le ha dato l'azienda («Magazzino Villacidro»), se c'è. */
  nome: string | null;
  citta: string | null;
  provincia: string | null;
  /** Solo nella console: l'indirizzo su una riga. Il link condiviso non lo porta. */
  indirizzo: string | null;
  posizione: PosizioneSullaMappa;
  /** La sede principale (`is_default`, altrimenti la prima): porta il segno grande dell'azienda. */
  principale: boolean;
  /** Vero quando è la sede legale, usata perché l'azienda non ha sedi operative. */
  legale: boolean;
}

/** Perché un'azienda non ha un punto sulla mappa (solo nella console: il link condiviso le lascia fuori). */
export type MotivoSenzaPosizione = 'senza_indirizzo' | 'indirizzo_non_trovato' | 'fuori_dall_italia' | 'posizione_non_calcolata';

/** Ciò che vede chi apre il link condiviso: nomi e loghi sì (decisione di Simone del 27/10/2026), soldi no. */
export interface AziendaPubblica {
  /** Una chiave opaca, stabile: non è l'id dell'azienda. */
  chiave: string;
  nome: string;
  /** Città e provincia della sede principale. */
  citta: string | null;
  provincia: string | null;
  /** Le sedi con un punto sulla mappa (vuoto = l'azienda non sta sulla mappa). La principale per prima. */
  sedi: SedeSullaMappa[];
  livello: LivelloMappa;
  /** La data di iscrizione (creazione del tenant), ISO. */
  iscritta_il: string | null;
  /** Gli account che occupano un posto, per livello. */
  account: ContiPerLivello;
  account_totale: number;
  /** Quante di quelle persone hanno usato il sistema negli ultimi `GIORNI_PERSONE_ATTIVE` giorni. */
  persone_attive: number;
  /** Quante di quelle persone stanno lavorando in questo momento (`statoDiPresenza` = in uso). */
  persone_adesso: number;
  ultima_attivita_il: string | null;
  logo_url: string | null;
  /**
   * Il logo QUADRATO, per i segni sulla carta e le tessere piccole: il logo compatto della barra laterale
   * (`branding.logo_sidebar_url`, «quadrato 48x48»), altrimenti la favicon, altrimenti il logo di WhatsApp (quadrato
   * anche lui). Solo indirizzi https.
   */
  logo_quadrato_url: string | null;
  /** Il colore principale del marchio, esadecimale. */
  colore: string | null;
}

export type StatoAbbonamentoMappa = 'esente' | 'in_prova' | 'pagante' | 'insoluto' | 'cancellato' | 'gratuito';

/** Dove sta lavorando una persona adesso: il gestionale (nel browser, anche da tablet) o un'app Android. */
export type DoveLavora = 'gestionale' | 'app';

/**
 * Una persona col gestionale aperto ma fermo: da quando (l'ultima azione vista; null = un gestionale vecchio, che non
 * lo dice). SOLO nella console, come `PersonaAlLavoro`.
 */
export interface PersonaConLaPaginaAperta {
  nome: string;
  email: string | null;
  ultima_azione_il: string | null;
}

/**
 * Una persona al lavoro adesso, col nome. SOLO nella console: sono dati personali dei dipendenti delle aziende
 * clienti, e il link condiviso porta nomi e loghi delle aziende, non delle persone (28/10/2026).
 */
export interface PersonaAlLavoro {
  nome: string;
  /** L'email dell'account: distingue gli account d'ufficio («Ufficio tecnico SSG») dalle persone. */
  email: string | null;
  dove: DoveLavora[];
}

/** Ciò che vede il super utente nella console. */
export interface AziendaSullaMappa extends AziendaPubblica {
  tenant_id: string;
  stato: StatoMappa;
  /** L'indirizzo della sede legale su una riga. */
  indirizzo: string | null;
  abbonamento: StatoAbbonamentoMappa | null;
  /** I posti acquistati; null = senza limite (aziende esenti) o non scritto. */
  posti_acquistati: number | null;
  /** Perché nessuna sede sta sulla mappa (null se almeno una c'è). */
  motivo_senza_posizione: MotivoSenzaPosizione | null;
  /** Le sedi operative scritte che non si sono potute mettere sulla mappa. */
  sedi_senza_posizione: number;
  /** Chi sta lavorando adesso (in ordine di nome); il loro numero è `persone_adesso`. */
  al_lavoro_adesso: PersonaAlLavoro[];
  /** Chi ha il gestionale aperto ma fermo da più di `MINUTI_SENZA_AZIONI` minuti (in ordine di nome). */
  pagina_aperta: PersonaConLaPaginaAperta[];
  /** Chi la rifornisce di materiale (id delle aziende fornitrici, `fornitoriDellAzienda`). */
  fornitori: string[];
  /** Chi l'ha portata su YumaOS (programma partner): l'arco sulla carta parte dall'azienda del partner. */
  portata_da: PortataDa | null;
}

/**
 * Le aziende del link condiviso: solo le attive con un punto sulla mappa, e di ognuna SOLO i campi pubblici. I campi
 * si ricopiano uno per uno di proposito: un campo nuovo aggiunto alla vista della console non deve finire nel link
 * senza che qualcuno lo decida.
 */
export function aziendeDelLinkCondiviso(
  aziende: readonly AziendaSullaMappa[],
  chiaveDi: (tenantId: string) => string,
): AziendaPubblica[] {
  return aziende
    .filter((a) => a.stato === 'attiva' && a.sedi.length > 0)
    .map((a) => ({
      chiave: chiaveDi(a.tenant_id),
      nome: a.nome,
      citta: a.citta,
      provincia: a.provincia,
      // Delle sedi passano nome, città e punto: l'indirizzo resta nella console.
      sedi: a.sedi.map((s) => ({ ...s, posizione: { ...s.posizione }, indirizzo: null })),
      livello: a.livello,
      iscritta_il: a.iscritta_il,
      account: { ...a.account },
      account_totale: a.account_totale,
      persone_attive: a.persone_attive,
      persone_adesso: a.persone_adesso,
      ultima_attivita_il: a.ultima_attivita_il,
      logo_url: a.logo_url,
      logo_quadrato_url: a.logo_quadrato_url,
      colore: a.colore,
    }));
}

// ───────────────────────────────────────────────────────────── il link condiviso

/** Dove porta il link: la console, rotta pubblica fuori dall'accesso. */
export const BASE_MAPPA_CONDIVISA = 'https://console.yumaos.com';
export const PERCORSO_MAPPA_CONDIVISA = '/mappa/';

export const DURATE_CONDIVISIONE_GIORNI = [7, 30, 90] as const;
export type DurataCondivisione = (typeof DURATE_CONDIVISIONE_GIORNI)[number];
export const DURATA_CONDIVISIONE_PREDEFINITA: DurataCondivisione = 30;

/** «Per chi è» il link: serve a riconoscerlo nell'elenco, non lo vede chi lo apre. */
export const DESTINATARIO_MASSIMO = 80;

/** I codici sono 24 byte casuali in base64url: 32 caratteri. */
const CODICE = /^[A-Za-z0-9_-]{32}$/;

export function codiceCondivisioneValido(codice: unknown): codice is string {
  return typeof codice === 'string' && CODICE.test(codice);
}

export function durataValida(giorni: unknown): giorni is DurataCondivisione {
  return (DURATE_CONDIVISIONE_GIORNI as readonly unknown[]).includes(giorni);
}

export function linkMappaCondivisa(codice: string, base: string = BASE_MAPPA_CONDIVISA): string {
  return `${base.replace(/\/+$/, '')}${PERCORSO_MAPPA_CONDIVISA}${encodeURIComponent(codice)}`;
}

export type StatoCondivisione = 'attiva' | 'scaduta' | 'ritirata';

export interface CondivisioneMappa {
  codice: string;
  link: string;
  destinatario: string;
  creata_il: string;
  scade_il: string;
  ritirata_il: string | null;
  aperture: number;
  ultima_apertura_il: string | null;
  stato: StatoCondivisione;
}

export function statoDellaCondivisione(
  c: { scade_il: string; ritirata_il: string | null },
  adesso: Date,
): StatoCondivisione {
  if (c.ritirata_il) return 'ritirata';
  const scade = Date.parse(c.scade_il);
  return Number.isFinite(scade) && scade > adesso.getTime() ? 'attiva' : 'scaduta';
}

// ───────────────────────────────────────────────────────────── le porte

export const CALLABLE_MAPPA_AZIENDE = {
  console: 'consoleMappaAziende',
  condivisione: 'consoleMappaCondivisione',
  pubblica: 'mappaAziendePubblica',
} as const;

export interface RispostaMappaConsole {
  aziende: AziendaSullaMappa[];
  condivisioni: CondivisioneMappa[];
  calcolata_il: string;
  giorni_persone_attive: number;
  minuti_adesso: number;
}

export type RichiestaCondivisione =
  | { azione: 'crea'; destinatario: string; giorni: DurataCondivisione }
  | { azione: 'ritira'; codice: string };

export interface RispostaCondivisione {
  condivisione: CondivisioneMappa;
}

/** Un link scaduto, ritirato o sconosciuto risponde allo stesso modo: nessun motivo a chi non dovrebbe sapere. */
export type RispostaMappaPubblica =
  | { attiva: false }
  | { attiva: true; aziende: AziendaPubblica[]; calcolata_il: string; giorni_persone_attive: number; minuti_adesso: number; scade_il: string };
